import {
  BRAND_SEARCH_INITIATIVE_TYPE,
  DEFAULT_BRAND_SEARCH_POLICY,
  type BrandSearchPolicy,
  type RecommendationRow,
  type RowLock,
} from './types';

/**
 * A saved row setting as the recommender reads it (GET
 * /budget-recommender/row-settings, the same rows the API service reads).
 * Dates arrive as ISO strings over HTTP and as Date objects from Prisma.
 */
export interface RowControlSettingLike {
  entityId: string;
  pinned?: boolean | null;
  pinReason?: string | null;
  /** Email of whoever set the pin (resolved by the API from pinnedByUserId). */
  pinnedBy?: string | null;
  pinnedAt?: string | Date | null;
  pinExpiresAt?: string | Date | null;
  stepCapPct?: number | null;
}

/** Everything outside the performance math that decides whether, and how far, a row may move. */
export interface RowControls {
  /** Saved per-row settings: pins and step cap overrides. */
  settings?: ReadonlyArray<RowControlSettingLike>;
  /** Financial Settings `brandSearchPolicy`; anything unrecognized reads as the default ('fixed'). */
  brandSearchPolicy?: string | null;
  /** Financial Settings `maxBudgetStepPct`; null / absent = no company cap. */
  maxBudgetStepPct?: number | null;
  /** Clock for pin expiry; injectable for tests. */
  nowMs: number;
}

/** The reason a brand-policy lock carries. */
export const BRAND_SEARCH_POLICY_REASON = 'brand-search policy';

export function resolveBrandSearchPolicy(value: unknown): BrandSearchPolicy {
  return value === 'optimize' || value === 'fixed' ? value : DEFAULT_BRAND_SEARCH_POLICY;
}

const toIso = (value: string | Date | null | undefined): string | null => {
  if (value === null || value === undefined) return null;
  return value instanceof Date ? value.toISOString() : String(value);
};

/**
 * A pin holds until it is unpinned or its expiry passes. A row whose
 * `pinExpiresAt` is at or before now loads as a normal row again (the
 * test-cell case: pin for the test window). An expiry that cannot be read
 * keeps the pin — a pin that silently fell off would let a run move a row
 * someone meant to hold.
 */
export function isPinActive(setting: RowControlSettingLike | undefined, nowMs: number): boolean {
  if (!setting?.pinned) return false;
  if (setting.pinExpiresAt === null || setting.pinExpiresAt === undefined) return true;
  const expires = new Date(setting.pinExpiresAt).getTime();
  return Number.isNaN(expires) || expires > nowMs;
}

const isValidCap = (cap: number | null | undefined): cap is number => typeof cap === 'number' && Number.isFinite(cap) && cap > 0 && cap < 1;

/** The row's lock, if any. A manual pin wins over the brand-search policy for display. */
export function rowLockFor(
  row: Pick<RecommendationRow, 'initiativeType'>,
  setting: RowControlSettingLike | undefined,
  policy: BrandSearchPolicy,
  nowMs: number,
): RowLock | undefined {
  if (setting && isPinActive(setting, nowMs)) {
    return {
      source: 'pin',
      reason: setting.pinReason ?? null,
      pinnedBy: setting.pinnedBy ?? null,
      pinnedAt: toIso(setting.pinnedAt),
      expiresAt: toIso(setting.pinExpiresAt),
    };
  }
  if (policy === 'fixed' && row.initiativeType === BRAND_SEARCH_INITIATIVE_TYPE) {
    return { source: 'brand-policy', reason: BRAND_SEARCH_POLICY_REASON, pinnedBy: null, pinnedAt: null, expiresAt: null };
  }
  return undefined;
}

/**
 * Attach each row's lock and step cap before the scaling pass. Rows that
 * neither applies to are returned as is (same object); the others are
 * shallow copies. Pure: the input rows are not mutated.
 *
 * Settings match rows by entity id, like the saved analysis period.
 */
export function applyRowControls(rows: readonly RecommendationRow[], controls: RowControls): RecommendationRow[] {
  const byEntity = new Map<string, RowControlSettingLike>();
  for (const s of controls.settings ?? []) byEntity.set(s.entityId, s);
  const policy = resolveBrandSearchPolicy(controls.brandSearchPolicy);
  const companyCap = isValidCap(controls.maxBudgetStepPct) ? controls.maxBudgetStepPct : undefined;

  return rows.map((row) => {
    const setting = byEntity.get(row.entityId);
    const lock = rowLockFor(row, setting, policy, controls.nowMs);
    const rowCap = isValidCap(setting?.stepCapPct) ? setting?.stepCapPct : undefined;
    const stepCapPct = rowCap ?? companyCap;
    if (!lock && stepCapPct === undefined && row.lock === undefined && row.stepCapPct === undefined) return row;
    const next: RecommendationRow = { ...row };
    delete next.lock;
    delete next.stepCapPct;
    if (lock) next.lock = lock;
    if (stepCapPct !== undefined) next.stepCapPct = stepCapPct;
    return next;
  });
}
